import { Layout } from "components2";
import { ALink, BackButton2, NextButton2, StepperBar } from "components2/CommonUI";
import { APPT_TYPE, MODE_OF_CONTACT } from "constants/o2Appointment";
import { useAdobeDigitalData } from "hooks";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { clearContactDetail, updateError, updateModeOfContact, updateOtp, updateOtpToken } from "redux/o2Appointment";
import { getAliveCount } from "services/api-service2";
import { isValidOTP } from "utils/common2";
import { O2APPOINTMENT_ENTRY, O2APPOINTMENT_REVIEW } from "../constants/route";
import { InputEmail, InputMobileNumber, SelectEmail, SelectMobileNumber, SelectModeOfContact } from "./o2ContactDetailComponents";

// Back button can go to only O2APPOINTMENT_ENTRY
export default function O2ContactDetail() {
  const navigate = useNavigate();
  const {
    apptType,
    mobileUseUCD,
    emailUseUCD,
    otp,
    mobileNumberFromUCD,
    emailFromUCD,
    modeOfContact,
    otpToken
  } = useSelector((state) => state.o2Appointment);
  const { isLogin } = useSelector((state) => state.signPassIdentify);
  const refInputMobile = useRef();
  const refInputEmail = useRef();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  useAdobeDigitalData({name: "CONTACT_DETAIL", path:"/contact-detail"});

  const location = useLocation();

  useEffect(()=>{
    if(apptType=== APPT_TYPE.PHONE && modeOfContact === null) {
      dispatch(updateModeOfContact(MODE_OF_CONTACT.PHONE));
    }
  },[apptType, modeOfContact]);

  useEffect(() => {
    // appointment-entry page has long content, scroll to top when enter this page
    document.body.scrollTo({ top: 0 });

    // dispatch(clearContactDetail());

    return () => {};
  }, []);

  const [stepperItems, setStepperItems] = useState([
    { text: "Appointment details", current: false, complete: true, link: O2APPOINTMENT_ENTRY },
    { text: "Contact details", current: true, complete: false },
    { text: "Review", current: false, complete: false }
  ]);

  const clkBack = () => {
    navigate(O2APPOINTMENT_ENTRY);
  };

  const clkNext = () => {
    if(loading) {
      return;
    }
    setLoading(true);

    if(refInputMobile.current) {
      if(otpToken) {
        navigate(O2APPOINTMENT_REVIEW);
        return;
      }
      refInputMobile.current.validateOtp().then(isValid=>{
        if(isValid) {
          dispatch(updateOtp(null));
          navigate(O2APPOINTMENT_REVIEW);
        } else {
          setLoading(false);
        }
      });
    } else if(refInputEmail.current) {
      if(otpToken) {
        navigate(O2APPOINTMENT_REVIEW);
        return;
      }
      refInputEmail.current.validateOtp().then(isValid=>{
        if(isValid) {
          dispatch(updateOtp(null));
          navigate(O2APPOINTMENT_REVIEW);
        } else {
          setLoading(false);
        }
      })
    } else {
      // mobileUseUCD, mobileNumberFromUCD || emailUseUCD, emailFromUCD
      if(mobileUseUCD) {
        getAliveCount({ mobile: mobileNumberFromUCD, email: null }).then(({ count, exceed }) => {
          if(exceed) {
            setLoading(false);
            dispatch(updateError(`This contact is currently in use for ${count} appointment bookings. Please use another contact.`))
          } else {
            navigate(O2APPOINTMENT_REVIEW);
          }
        });
      }
      if(emailUseUCD) {
        getAliveCount({ mobile: null, email: emailFromUCD }).then(({ count, exceed }) => {
          if(exceed) {
            setLoading(false);
            dispatch(updateError(`This contact is currently in use for ${count} appointment bookings. Please use another contact.`))
          } else {
            navigate(O2APPOINTMENT_REVIEW);
          }
        });
      }
    }
  };

  const isNextDisabled = () => {
    if(apptType === APPT_TYPE.PHONE) {
      if (mobileUseUCD) {
        return false;
      } else if(otpToken !== null) {
        return false;
      } else if (!isValidOTP(otp)) {
        return true;
      }
      return false;
    } else if(apptType === APPT_TYPE.VISIT) {
      if(modeOfContact === MODE_OF_CONTACT.EMAIL) {
        if(emailUseUCD) {
          return false;
        } else if(otpToken !== null) {
          return false;
        } else if (!isValidOTP(otp)) {
          return true;
        }
        return false;
      } else if(modeOfContact === MODE_OF_CONTACT.PHONE) {
        if(mobileUseUCD) {
          return false;
        } else if(otpToken !== null) {
          return false;
        } else if (!isValidOTP(otp)) {
          return true;
        }
        return false;
      }
    }
    
    return true;
  };

  return (
    <Layout
      subHeader={"Book an appointment"}
      mainText={
        <StepperBar
          title={<span style={{ whiteSpace: "normal" }}>Book an appointment</span>}
          items={stepperItems}
        />
      }>
      <div>
        <div className="form-box--plain">
          <div className={"px-1 px-3 pt-64 md-pt-48 sm-pt-48"}>
            <h4 className={"text-center sm-mb-32 md-mb-32 lg-mb-48 xl-mb-48 lg-fs-32 xl-fs-32"}>Select mode of contact</h4>
            <div className="row justify-content-center mb-3">
              <div className="col-12 col-md-8 col-lg-8 py-2 text-center">
                Appointment details will be sent to this contact. Use the same contact details to
                view or reschedule your appointment later.
              </div>
            </div>
            {apptType === APPT_TYPE.VISIT && (
              <SelectModeOfContact />
            )}

            {isLogin && (apptType === APPT_TYPE.PHONE || (apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.PHONE)) && (
              <>
                <SelectMobileNumber />
              </>
            )}
            {isLogin && apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.EMAIL && (
              <>
                <SelectEmail />
              </>
            )}
            {isLogin && (apptType === APPT_TYPE.PHONE || (apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.PHONE)) && !mobileUseUCD && (
              <>
                {mobileNumberFromUCD !== null && (
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <hr />
                    </div>
                  </div>
                )}

                <InputMobileNumber ref={refInputMobile} />
              </>
            )}
            {isLogin && apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.EMAIL && !emailUseUCD && (
              <>
                {mobileNumberFromUCD !== null && (
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <hr />
                    </div>
                  </div>
                )}

                <InputEmail ref={refInputEmail} />
              </>
            )}

            {!isLogin && apptType === APPT_TYPE.PHONE && (
              <>
                <InputMobileNumber ref={refInputMobile} />
              </>
            )}

            {!isLogin && apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.PHONE && (
              <>
                <InputMobileNumber ref={refInputMobile} />
              </>
            )}


            {!isLogin && apptType === APPT_TYPE.VISIT && modeOfContact === MODE_OF_CONTACT.EMAIL && (
              <>
                <InputEmail ref={refInputEmail} />
              </>
            )}

            {isLogin && apptType === APPT_TYPE.VISIT && <></>}
            {!isLogin && apptType === APPT_TYPE.VISIT && <></>}

            <div className="instructions instructions-mod sm-mt-32 md-mt-32 lg-mt-52 xl-mt-52 sm-mb-28 md-mb-28 lg-mb-24 xl-mb-24">
              <div className="instructions-content">
                <ul style={{ marginBottom: 0 }}>
                  <li>
                    Problems booking an appointment? <ALink target={"_blank"} href={window.callUsLink}>Call us</ALink> or{" "}
                    <ALink target={"_blank"} href={window.writeToUsLink}>write to us</ALink>.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="form-footer">
            {/*<div className={"flex--space-between"}>*/}
            <div
              className={
                "w-100 d-flex flex-column-reverse flex-md-row justify-content-between px-md-3 py-2 align-items-start "
              }>
              <div className={"mobile-w-100"}>
                <BackButton2 onClick={clkBack} />
              </div>
              <div className={"mb-3 mb-md-0 mobile-w-100"}>
                <NextButton2 disabled={isNextDisabled()} onClick={clkNext} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
